import { useRef, useState, useEffect, useCallback } from 'react';

export interface UseFakeCaretOptions {
    showCaretWhenBlur?: boolean;
    isCaretVisible?: boolean;
    onCaretVisibilityChange?: (visible: boolean) => void;
}

export function useFakeCaret(options: UseFakeCaretOptions = {}) {
    const { showCaretWhenBlur = false, isCaretVisible: controlledVisible, onCaretVisibilityChange } = options;

    const inputRef = useRef<HTMLInputElement>(null);
    const [caretPosition, setCaretPosition] = useState(0);
    const [isFocused, setIsFocused] = useState(false);
    const [scrollOffset, setScrollOffset] = useState(0);
    const [internalCaretVisible, setInternalCaretVisible] = useState(showCaretWhenBlur);

    const isControlled = controlledVisible !== undefined;
    const isCaretVisible = isControlled ? controlledVisible : internalCaretVisible;

    const updateCaretPosition = useCallback(() => {
        if (inputRef.current) {
            setCaretPosition(inputRef.current.selectionStart || 0);
            setScrollOffset(inputRef.current.scrollLeft);
        }
    }, []);

    const calculateCaretPixelPosition = () => {
        if (!inputRef.current) return 0;

        const input = inputRef.current;
        const text = input.value.substring(0, caretPosition);
        const computedStyle = window.getComputedStyle(input);

        // Get input's position and styles
        const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
        const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
        const borderLeft = parseFloat(computedStyle.borderLeftWidth) || 0;

        // Create a mirror element to measure text width
        const mirror = document.createElement('span');

        mirror.style.position = 'absolute';
        mirror.style.visibility = 'hidden';
        mirror.style.whiteSpace = 'pre';
        mirror.style.font = computedStyle.font;
        mirror.style.fontSize = computedStyle.fontSize;
        mirror.style.fontFamily = computedStyle.fontFamily;
        mirror.style.fontWeight = computedStyle.fontWeight;
        mirror.style.fontStyle = computedStyle.fontStyle;
        mirror.style.letterSpacing = computedStyle.letterSpacing;
        mirror.style.wordSpacing = computedStyle.wordSpacing;
        mirror.style.textTransform = computedStyle.textTransform;
        mirror.style.lineHeight = computedStyle.lineHeight;

        mirror.textContent = text || '';

        document.body.appendChild(mirror);
        const textWidth = mirror.getBoundingClientRect().width;
        document.body.removeChild(mirror);

        // Calculate the caret position relative to the input's parent container
        const inputRect = input.getBoundingClientRect();
        const parentRect = input.parentElement?.getBoundingClientRect();
        const caretFromInputOuterLeft = borderLeft + paddingLeft + textWidth - scrollOffset;
        const minCaretFromOuterLeft = borderLeft + paddingLeft;
        const maxCaretFromOuterLeft = borderLeft + Math.max(0, input.clientWidth - paddingRight);
        const clampedCaretFromOuterLeft = Math.min(Math.max(caretFromInputOuterLeft, minCaretFromOuterLeft), maxCaretFromOuterLeft);

        if (parentRect) {
            const offsetFromParent = inputRect.left - parentRect.left;
            return offsetFromParent + clampedCaretFromOuterLeft;
        }

        return clampedCaretFromOuterLeft;
    };

    const updateCaretVisibility = useCallback(
        (visible: boolean) => {
            if (isControlled) {
                onCaretVisibilityChange?.(visible);
            } else {
                setInternalCaretVisible(visible);
            }
        },
        [isControlled, onCaretVisibilityChange]
    );

    // Update caret position when input value changes externally
    useEffect(() => {
        updateCaretPosition();
    }, [updateCaretPosition]);

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;

        const handleClick = () => {
            updateCaretPosition();
            if (!isControlled) {
                updateCaretVisibility(true);
            }
        };
        const handleKeyUp = () => {
            updateCaretPosition();
            if (!isControlled) {
                updateCaretVisibility(true);
            }
        };
        const handleKeyDown = () => {
            updateCaretPosition();
            if (!isControlled) {
                updateCaretVisibility(true);
            }
        };
        const handleScroll = updateCaretPosition;
        const handleFocus = () => {
            setIsFocused(true);
            updateCaretPosition();
            if (!isControlled) {
                updateCaretVisibility(true);
            }
        };
        const handleBlur = () => {
            setIsFocused(false);
            if (!isControlled && !showCaretWhenBlur) {
                updateCaretVisibility(false);
            }
        };

        input.addEventListener('click', handleClick);
        input.addEventListener('keyup', handleKeyUp);
        input.addEventListener('keydown', handleKeyDown);
        input.addEventListener('scroll', handleScroll);
        input.addEventListener('focus', handleFocus);
        input.addEventListener('blur', handleBlur);

        return () => {
            input.removeEventListener('click', handleClick);
            input.removeEventListener('keyup', handleKeyUp);
            input.removeEventListener('keydown', handleKeyDown);
            input.removeEventListener('scroll', handleScroll);
            input.removeEventListener('focus', handleFocus);
            input.removeEventListener('blur', handleBlur);
        };
    }, [showCaretWhenBlur, isControlled, updateCaretVisibility, updateCaretPosition]);

    const caretPixelPosition = calculateCaretPixelPosition();

    const showCaret = () => updateCaretVisibility(true);
    const hideCaret = () => updateCaretVisibility(false);

    return {
        inputRef,
        caretPosition,
        isFocused,
        isCaretVisible,
        scrollOffset,
        caretPixelPosition,
        updateCaretPosition,
        showCaret,
        hideCaret,
    };
}
